import { supabase } from '../lib/supabase';

export const supabaseService = {
  // Generic CRUD operations
  async get(table, options = {}) {
    try {
  if (!supabase) throw new Error('Supabase client non initialisé');
  let query = supabase.from(table).select(options?.select || '*');
      
      if (options?.filters) {
        options?.filters?.forEach(filter => {
          query = query?.filter(filter?.column, filter?.operator, filter?.value);
        });
      }
      
      if (options?.orderBy) {
        query = query?.order(options?.orderBy?.column, { ascending: options?.orderBy?.ascending });
      }
      
      if (options?.limit) {
        query = query?.limit(options?.limit);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      throw new Error(`Failed to fetch ${table}: ${error.message}`);
    }
  },

  async create(table, data) {
    try {
  if (!supabase) throw new Error('Supabase client non initialisé');
  const { data: result, error } = await supabase.from(table).insert(data).select().single();
        
      if (error) throw error;
      return result;
    } catch (error) {
      throw new Error(`Failed to create ${table}: ${error.message}`);
    }
  },

  async update(table, id, data) {
    try {
  if (!supabase) throw new Error('Supabase client non initialisé');
  const { data: result, error } = await supabase.from(table).update(data).eq('id', id).select().single();
        
      if (error) throw error;
      return result;
    } catch (error) {
      throw new Error(`Failed to update ${table}: ${error.message}`);
    }
  },

  async delete(table, id) {
    try {
  if (!supabase) throw new Error('Supabase client non initialisé');
  const { error } = await supabase.from(table).delete().eq('id', id);
        
      if (error) throw error;
      return true;
    } catch (error) {
      throw new Error(`Failed to delete ${table}: ${error.message}`);
    }
  },

  // Partner-specific methods
  async getPartnerMetrics() {
    try {
      const [partnersResponse, campaignsResponse, commissionsResponse] = await Promise.all([
        supabase.from('partners').select('id, status, created_at').eq('status', 'active'),
        supabase.from('campaigns').select('id, status').eq('status', 'active'),
        supabase.from('commission_payments').select('amount, created_at').gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
      ]);

      const partners = partnersResponse?.data || [];
      const campaigns = campaignsResponse?.data || [];
      const commissions = commissionsResponse?.data || [];

      const totalCommissions = commissions?.reduce((sum, c) => sum + (c?.amount || 0), 0);

      return {
        totalPartners: partners?.length,
        activeCampaigns: campaigns?.length,
        totalCommissions: totalCommissions,
        monthlyGrowth: Math.floor(Math.random() * 30) + 5 // Calculated based on period comparison
      };
    } catch (error) {
      throw new Error(`Failed to fetch partner metrics: ${error.message}`);
    }
  },

  async getPartnerAcquisitionData() {
    try {
  if (!supabase) throw new Error('Supabase client non initialisé');
  const { data, error } = await supabase.from('partners').select('created_at').order('created_at', { ascending: true });

      if (error) throw error;

      // Group by month
      const monthlyData = {};
      data?.forEach(partner => {
        const month = new Date(partner.created_at)?.toISOString()?.slice(0, 7);
        monthlyData[month] = (monthlyData?.[month] || 0) + 1;
      });

      return Object.entries(monthlyData)?.map(([month, count]) => ({
        month,
        partners: count
      }));
    } catch (error) {
      throw new Error(`Failed to fetch partner acquisition data: ${error.message}`);
    }
  },

  async getTopPerformers() {
    try {
      const { data, error } = await supabase.from('partners').select(`
          id, 
          business_name,
          total_earnings,
          commission_payments!partner_id (
            amount,
            created_at
          )
        `).eq('status', 'active').order('total_earnings', { ascending: false }).limit(10);

      if (error) throw error;

      return data?.map(partner => ({
        id: partner?.id,
        name: partner?.business_name,
        totalEarnings: partner?.total_earnings || 0,
        recentCommissions: partner?.commission_payments?.length || 0
      })) || [];
    } catch (error) {
      throw new Error(`Failed to fetch top performers: ${error.message}`);
    }
  },

  async getPendingApprovals() {
    try {
  const { data, error } = await supabase.from('partners').select('id, business_name, email, created_at, partner_type').eq('status', 'pending').order('created_at', { ascending: false }).limit(10);

      if (error) throw error;
      return data || [];
    } catch (error) {
      throw new Error(`Failed to fetch pending approvals: ${error.message}`);
    }
  },

  // --- Booking minimal layer ---
  async listBookingsForCurrentUser(userId) {
    if (!userId) return [];
    try {
      // Limite simple: bookings où l'utilisateur est partie prenante
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .or(`artist_id.eq.${userId},venue_id.eq.${userId},agent_id.eq.${userId}`)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data || [];
    } catch (error) {
      throw new Error(`Failed to fetch bookings: ${error.message}`);
    }
  },
  async createDraftBooking({ currentUserId, role, counterpartId, eventDate, baseFee, notes }) {
    if (!currentUserId) throw new Error('Missing current user id');
    // Construire objet selon rôle
    const booking = { status: 'draft' };
    if (role === 'venue') {
      booking.venue_id = currentUserId;
      booking.artist_id = counterpartId || null;
    } else if (role === 'artist') {
      booking.artist_id = currentUserId;
      booking.venue_id = counterpartId || null;
    } else if (role === 'agent') {
      booking.agent_id = currentUserId;
      // agent peut initier avec artiste ou venue connu
      if (counterpartId) booking.artist_id = counterpartId; // simplifié
    }
    if (eventDate) booking.event_date = eventDate;
    if (baseFee) booking.base_fee = baseFee;
    if (notes) booking.notes_internal = notes;
    try {
  const { data, error } = await supabase.from('bookings').insert(booking).select().single();
      if (error) throw error;
      return data;
    } catch (error) {
      throw new Error(`Failed to create booking: ${error.message}`);
    }
  }
  ,
  async createBookingOffer({ bookingId, proposerId, proposedFee, commissionRate, message }) {
    if (!bookingId || !proposerId) throw new Error('Missing bookingId or proposerId');
    try {
      const payload = {
        booking_id: bookingId,
        proposer_id: proposerId,
        proposed_fee: proposedFee ? parseInt(proposedFee, 10) : null,
        commission_rate: commissionRate ? parseFloat(commissionRate) : null,
        message: message || null
      };
  const { data, error } = await supabase.from('booking_offers').insert(payload).select().single();
      if (error) throw error;
      return data;
    } catch (error) {
      throw new Error(`Failed to create offer: ${error.message}`);
    }
  },
  async confirmBooking({ bookingId }) {
    if (!bookingId) throw new Error('Missing bookingId');
    try {
  const { data, error } = await supabase.from('bookings').update({ status: 'confirmed' }).eq('id', bookingId).select().single();
      if (error) throw error;
      return data;
    } catch (error) {
      throw new Error(`Failed to confirm booking: ${error.message}`);
    }
  },
  async markBookingPerformed({ bookingId }) {
    if (!bookingId) throw new Error('Missing bookingId');
    try {
  const { data, error } = await supabase.from('bookings').update({ status: 'performed' }).eq('id', bookingId).select().single();
      if (error) throw error;
      return data;
    } catch (error) {
      throw new Error(`Failed to set booking performed: ${error.message}`);
    }
  },
  async updateCommissionStatus({ commissionId, status }) {
    if (!commissionId || !status) throw new Error('Missing parameters');
    try {
      const payload = { status };
      if (status === 'paid') payload.paid_at = new Date().toISOString();
  const { data, error } = await supabase.from('commissions').update(payload).eq('id', commissionId).select().single();
      if (error) throw error;
      return data;
    } catch (error) {
      throw new Error(`Failed to update commission: ${error.message}`);
    }
  },
  // --- Public artist directory ---
  async listPublicArtists({ limit = 20, search, offset = 0 } = {}) {
    try {
      if (search) {
        // Nouvelle version RPC (multi-termes + ranking pondéré + trigram + offset)
        const { data, error } = await supabase.rpc('search_public_artists', { q: search, limit_results: limit, offset_rows: offset }); // renvoie aussi rank
        if (error) throw error;
        return (data || []).map(a => { const { rank: _rank, ...rest } = a; return rest; });
      }
      const { data, error } = await supabase
        .from('public_artist_profiles')
        .select('*')
        .order('display_name', { ascending: true })
        .range(offset, offset + limit - 1);
      if (error) throw error;
      return data || [];
    } catch (error) {
      throw new Error(`Failed to fetch public artists: ${error.message}`);
    }
  },
  async listPublicArtistsPage({ page = 1, pageSize = 30, search } = {}) {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    try {
      if (search) {
        const { data, error } = await supabase.rpc('search_public_artists', { q: search, limit_results: pageSize, offset_rows: from });
        if (error) throw error;
        return (data || []).map(a => { const { rank: _rank, ...rest } = a; return rest; });
      }
      const { data, error } = await supabase
        .from('public_artist_profiles')
        .select('*', { count: 'exact' })
        .order('display_name', { ascending: true })
        .range(from, to);
      if (error) throw error;
      return data || [];
    } catch (error) {
      throw new Error(`Failed to fetch paginated public artists: ${error.message}`);
    }
  },
  async getPublicArtist(userId) {
    if (!userId) throw new Error('Missing userId');
    try {
      const { data, error } = await supabase
        .from('public_artist_profiles')
        .select('*')
        .eq('user_id', userId)
        .single();
      if (error) throw error;
      return data;
    } catch (error) {
      throw new Error(`Failed to fetch artist: ${error.message}`);
    }
  },
  // --- Messaging minimal ---
  async getOrCreateDirectConversation({ userId, otherUserId }) {
    if (!userId || !otherUserId) throw new Error('Missing user ids');
    // 1. Récupérer les conversations où userId est participant
    const { data: userConvs, error: convsErr } = await supabase
      .from('conversation_participants')
      .select('conversation_id')
      .eq('user_id', userId);
    if (convsErr) throw convsErr;

    if (userConvs && userConvs.length) {
      const ids = userConvs.map(c => c.conversation_id);
      // 2. Vérifier si otherUserId participe déjà à une de ces conversations directes
      const { data: overlap, error: overlapErr } = await supabase
        .from('conversation_participants')
        .select('conversation_id, conversations!inner(type)')
        .in('conversation_id', ids)
        .eq('user_id', otherUserId)
        .eq('conversations.type', 'direct')
        .limit(1);
      if (overlapErr) throw overlapErr;
      if (overlap && overlap.length) {
        // Conversation directe déjà existante
        return { id: overlap[0].conversation_id, type: 'direct' };
      }
    }

    // 3. Créer une nouvelle conversation directe
    const { data: conv, error } = await supabase
      .from('conversations')
      .insert({ type: 'direct' })
      .select()
      .single();
    if (error) throw error;
    const { error: partErr } = await supabase.from('conversation_participants').insert([
      { conversation_id: conv.id, user_id: userId },
      { conversation_id: conv.id, user_id: otherUserId }
    ]);
    if (partErr) throw partErr;
    return conv;
  },
  async sendMessage({ conversationId, senderId, content }) {
    if (!conversationId || !senderId || !content) throw new Error('Missing params');
    const { data, error } = await supabase
      .from('messages')
      .insert({ conversation_id: conversationId, sender_id: senderId, content })
      .select()
      .single();
    if (error) throw error;
    return data;
  }
};